// ShareDialog.tsx — the owner's sharing controls for one scan (ARCHITECTURE.md
// §6), in the Google Docs shape: people with access (the owner, then each
// email grant with a viewer/editor role that can be changed or removed, and
// an add-by-email form), and share links (each a token URL with a role, an
// optional label and expiry; copy, revoke, an Expired badge). Every change
// answers the scan's whole Sharing, which replaces the dialog's state.
import { useCallback, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { BsClipboard, BsLink45Deg, BsPlusLg, BsX } from 'react-icons/bs';
import { Badge, ui } from 'common';
import { api, shareUrl } from '../api';
import type { ShareLink, ShareRole, Sharing } from '../api';
import { errorMessage, fmtDate } from '../format';

const { Dropdown, Text } = ui;

const ROLE_OPTIONS: { value: ShareRole; label: string }[] = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
];

// EXPIRY_OPTIONS are link lifetimes in hours ('0' never expires).
export const EXPIRY_OPTIONS = [
  { value: '0', label: 'Never expires' },
  { value: '24', label: 'Expires in 1 day' },
  { value: '168', label: 'Expires in 7 days' },
  { value: '720', label: 'Expires in 30 days' },
];

const ROLE_HELP: Record<ShareRole, string> = {
  viewer: 'can view, measure and export',
  editor: 'can also rename, segment and add captures',
};

// validEmail is a light client-side check; the server has the final say.
export function validEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+$/.test(s.trim());
}

export interface ShareDialogProps {
  scanId: string;
  scanName: string;
  show: boolean;
  onHide: () => void;
  // onShared reports whether the scan now has any grants or links.
  onShared?: (shared: boolean) => void;
}

export function ShareDialog({ scanId, scanName, show, onHide, onShared }: ShareDialogProps) {
  const [sharing, setSharing] = useState<Sharing | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [grantRole, setGrantRole] = useState<ShareRole>('viewer');
  const [linkRole, setLinkRole] = useState<ShareRole>('viewer');
  const [expiry, setExpiry] = useState('0');
  const [label, setLabel] = useState('');
  const [copied, setCopied] = useState('');

  const apply = useCallback(
    (s: Sharing) => {
      setSharing(s);
      onShared?.(s.grants.length > 0 || s.links.length > 0);
    },
    [onShared],
  );

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setError('');
    setCopied('');
    api
      .getSharing(scanId)
      .then((s) => !cancelled && apply(s))
      .catch((err) => !cancelled && setError(errorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, [show, scanId, apply]);

  const run = async (what: string, fn: () => Promise<Sharing>) => {
    setBusy(true);
    setError('');
    try {
      apply(await fn());
      console.info(`[openscan/share] ${what} on ${scanId}`);
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addGrant = async () => {
    const e = email.trim().toLowerCase();
    if (!validEmail(e)) return;
    if (await run(`grant ${grantRole}`, () => api.grantAccess(scanId, e, grantRole))) setEmail('');
  };

  const createLink = async () => {
    if (await run(`link ${linkRole}`, () => api.createLink(scanId, linkRole, label.trim(), Number(expiry)))) setLabel('');
  };

  const copy = async (link: ShareLink) => {
    const url = shareUrl(scanId, link.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(link.id);
    } catch (err) {
      // Clipboard blocked (insecure origin, permissions): show the URL so it
      // can be copied by hand.
      console.warn('[openscan/share] clipboard write failed', err);
      setError(`Copy this link by hand: ${url}`);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="h5 m-0 text-truncate">Share "{scanName}"</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="text-break">
            {error}
          </Alert>
        )}
        {!sharing && !error && (
          <div className="d-flex justify-content-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {sharing && (
          <>
            <h2 className="h6">People with access</h2>
            <ul className="list-unstyled mb-2">
              <li className="d-flex align-items-center gap-2 py-1">
                <span className="text-truncate me-auto">{sharing.owner}</span>
                <span className="small text-body-secondary">Owner</span>
              </li>
              {sharing.grants.map((g) => (
                <li key={g.email} className="d-flex flex-wrap align-items-center gap-2 py-1">
                  <span className="text-truncate me-auto" title={`Added ${fmtDate(g.granted_at)} by ${g.granted_by}`}>
                    {g.email}
                  </span>
                  <Dropdown
                    value={g.role}
                    options={ROLE_OPTIONS}
                    disabled={busy}
                    aria-label={`Role for ${g.email}`}
                    onChange={(e) => {
                      const role = e.target.value as ShareRole;
                      void run(`grant ${role}`, () => api.grantAccess(scanId, g.email, role));
                    }}
                  />
                  <Button
                    size="sm"
                    variant="link"
                    disabled={busy}
                    aria-label={`Remove ${g.email}`}
                    onClick={() => void run('revoke grant', () => api.revokeAccess(scanId, g.email))}
                  >
                    <BsX className="me-1" />
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
            <Form
              className="d-flex flex-wrap align-items-center gap-2 mb-4"
              onSubmit={(e) => {
                e.preventDefault();
                void addGrant();
              }}
            >
              <Text
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                minWidth={24}
                aria-label="Email to share with"
              />
              <Dropdown value={grantRole} options={ROLE_OPTIONS} onChange={(e) => setGrantRole(e.target.value as ShareRole)} aria-label="Role to grant" />
              <Button type="submit" size="sm" variant="outline-primary" disabled={busy || !validEmail(email)}>
                <BsPlusLg className="me-1" />
                Add
              </Button>
              <span className="small text-body-secondary w-100">
                A {grantRole} {ROLE_HELP[grantRole]}. They sign in with their lab0 identity and find it under Shared with me.
              </span>
            </Form>

            <h2 className="h6">Share links</h2>
            <p className="small text-body-secondary">
              Anyone holding a link gets its role on this scan, signed in or not. Revoke a link to cut it off.
            </p>
            <ul className="list-unstyled mb-2">
              {sharing.links.length === 0 && <li className="small text-body-secondary py-1">No links yet.</li>}
              {sharing.links.map((l) => (
                <li key={l.id} className="d-flex flex-wrap align-items-center gap-2 py-1">
                  <BsLink45Deg />
                  <span className="me-auto">
                    {l.label || 'Link'} · {l.role === 'editor' ? 'Editor' : 'Viewer'}
                    <span className="small text-body-secondary">
                      {' '}
                      · {l.expires_at ? `${l.expired ? 'expired' : 'expires'} ${fmtDate(l.expires_at)}` : 'never expires'}
                    </span>
                  </span>
                  {l.expired && <Badge tone="warning">Expired</Badge>}
                  {!l.expired && (
                    <Button size="sm" variant="outline-secondary" onClick={() => void copy(l)}>
                      <BsClipboard className="me-1" />
                      {copied === l.id ? 'Copied' : 'Copy link'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="link"
                    disabled={busy}
                    aria-label={`Revoke ${l.label || 'link'}`}
                    onClick={() => void run('revoke link', () => api.revokeLink(scanId, l.id))}
                  >
                    <BsX className="me-1" />
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
            <Form
              className="d-flex flex-wrap align-items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void createLink();
              }}
            >
              <Dropdown value={linkRole} options={ROLE_OPTIONS} onChange={(e) => setLinkRole(e.target.value as ShareRole)} aria-label="Link role" />
              <Dropdown value={expiry} options={EXPIRY_OPTIONS} onChange={(e) => setExpiry(e.target.value)} aria-label="Link expiry" />
              <Text placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} minWidth={16} aria-label="Link label" />
              <Button type="submit" size="sm" variant="outline-primary" disabled={busy}>
                <BsPlusLg className="me-1" />
                Create link
              </Button>
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
